import { Typography } from "@mui/material";
import styles from "../../../../components/TableCard/styles.module.css";

/**
 * Mesma tipografia/cor que a tela de Usuários aplica inline ao título do TableCard
 * (iconHeader/titleHeader — ver src/pages/Usuarios/index.js). O CSS module do TableCard
 * só cuida do layout (flex/gap) do cabeçalho; quem define fonte/cor/peso é sempre quem
 * consome o componente. Centralizar aqui evita que cada aba precise repetir (e arriscar
 * divergir) esse sx a cada novo uso.
 */
export const TITULO_SX = { color: "#1b3a5f", fontSize: "1.625rem", letterSpacing: "-0.3px" };
export const ICONE_SX = { fontSize: 32, color: "#1b3a5f" };

/**
 * Shell visual "igual à tela de Usuários" (TableCard): um único card branco
 * arredondado e sombreado (dentro de um container cinza claro também arredondado),
 * contendo cabeçalho (ícone+título+subtítulo+ação), filtros, estatísticas, divisor,
 * conteúdo (tabela) e paginação — tudo dentro do MESMO card, inclusive a paginação.
 *
 * Reaproveita literalmente as classes de components/TableCard/styles.module.css (não
 * uma cópia) — garante paridade visual exata com Usuários/Grupos/TableCard em vez de
 * arriscar divergência com uma reimplementação parecida.
 *
 * `bare`: quando true, renderiza só header/estatísticas/divisor/conteúdo/paginação, sem
 * o container/innerCard externo — usado quando esta seção já vive dentro do innerCard de
 * um ApuracaoCardShell pai (ex.: cada aba de DetalheIcms, que compartilha UM único card
 * com o cabeçalho da apuração e os cards de KPI, em vez de abrir um card aninhado).
 */
export default function ApuracaoCardShell({
  bare = false,
  hideContainer = false,
  Icone,
  titulo,
  subtitulo,
  acaoHeader,
  filtros,
  estatisticas,
  estatisticasColunas,
  children,
  paginacao,
  headerSx,
  innerCardSx,
  tituloFontWeight = 700,
  tituloSx,
  // Tabelas largas (CNPJ/Competência fixos + rolagem, ver stickyKeyCol1/2 em
  // TableView/styles.module.css) já rolam horizontalmente por conta própria, dentro do
  // wrapper com borda que a própria aba desenha. Sem esta flag, `.visualizacaoContainer`
  // (abaixo) TAMBÉM tenta rolar a mesma tabela — duas barras de rolagem horizontais pra
  // rolar a mesma coisa. `false` mantém o comportamento de sempre pra quem não usa tabela
  // com rolagem própria (não muda nada pros outros consumidores deste componente).
  conteudoComScrollProprio = false,
}) {
  const conteudo = (
    <>
      <div className={styles.header} style={headerSx}>
        <div
          className={styles.title}
          style={{
            order: 1,
            ...(subtitulo ? { flexDirection: "column", alignItems: "flex-start", gap: 4 } : undefined),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {Icone && <Icone sx={ICONE_SX} />}
            <Typography variant="h5" fontWeight={tituloFontWeight} sx={{ ...TITULO_SX, ...tituloSx }}>
              {titulo}
            </Typography>
          </div>
          {subtitulo && (
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              {subtitulo}
            </Typography>
          )}
        </div>
        {acaoHeader && (
          <div style={{ order: 2, display: "flex", alignItems: "center" }}>{acaoHeader}</div>
        )}
        {filtros && (
          // order + flexBasis 100% força os filtros pra própria linha, sempre — não depende
          // de o conteúdo "não caber" na linha do título/ação pra decidir quebrar (isso
          // dependia da largura da janela e causava o layout espremido numa linha só).
          <div
            className={`${styles.filtrosContainer} ${styles.filtrosContainerFullWidth}`}
            style={{
              flex: "1 1 auto",
              flexBasis: "100%",
              order: 3,
              border: "none",
              margin: "8px 0 0",
              padding: "16px 20px",
              borderRadius: "10px",
              backgroundColor: "#f5f7fa",
            }}
          >
            {/* justifyContent flex-start sobrescreve o "center" do .headerCampos
                compartilhado (TableCard/Usuários) — aqui os campos alinham à esquerda,
                igual ao protótipo, em vez de centralizar o grupo na linha inteira. */}
            <div className={styles.headerCampos} style={{ justifyContent: "flex-start", width: "100%" }}>
              {/* width:100% aqui também (não só em .headerCampos) — sem isso .inputs_header
                  fica do tamanho do próprio conteúdo (shrink-to-fit), então um filtro que
                  tenta crescer (flex-grow) só divide o espaço que o conteúdo já ocupava, não
                  o espaço real disponível na caixa cinza. Mesmo ajuste que TableCard/index.js
                  já faz (customFiltersFullWidth) — inofensivo pra quem não usa campos que
                  crescem: sem background/borda própria, só alarga uma caixa invisível. */}
              <div className={styles.inputs_header} style={{ width: "100%" }}>{filtros}</div>
            </div>
          </div>
        )}
      </div>

      {estatisticas && (
        <div
          className={estatisticasColunas ? styles.contentAboveTableGrid4 : styles.contentAboveTable}
          style={
            estatisticasColunas
              ? { gridTemplateColumns: `repeat(${estatisticasColunas}, 1fr)` }
              : undefined
          }
        >
          {estatisticas}
        </div>
      )}

      <div className={styles.tableDivider} />

      <div
        className={
          conteudoComScrollProprio
            ? `${styles.visualizacaoContainer} ${styles.visualizacaoContainerSemScrollProprio}`
            : styles.visualizacaoContainer
        }
      >
        {children}
      </div>

      {paginacao}
    </>
  );

  if (bare) return conteudo;

  const innerCard = (
    <div className={styles.innerCard} style={innerCardSx}>
      {conteudo}
    </div>
  );

  // `hideContainer`: pula o `.container` (fundo cinza + margem próprios do TableCard) quando
  // o card já está dentro de um canvas cinza equivalente montado pelo próprio consumidor
  // (ex.: DetalheIcms, que replica a Box cinza da tela de Usuários) — evita cinza-dentro-de-
  // cinza com margem duplicada. Mantém o `.innerCard` (card branco com sombra) normalmente.
  if (hideContainer) return innerCard;

  return <div className={styles.container}>{innerCard}</div>;
}
